import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbPost, dbForm } from '../../apis/commonApi';
import DatePicker from '../../components/dateComponents/DatePicker';
import TextViewer from '../../components/inputComponents/TextViewer';
import TextEditor from '../../components/inputComponents/TextEditor';
import FileUpload from '../../components/modalComponents/FileUpload';
import GbitTablePaging from '../../components/tableComponents/GbitTablePaging';
import { GlobalContext } from '../../components/contexts';
import { nlPost } from '../../apis/notLoginApi';

const QuestionList = () => {
  useEffect(() => {
    document.title = '문의 목록';
  }, []);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [questions, setQuestions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isDetailView, setIsDetailView] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [ansRegDt, setAnsRegDt] = useState('');
  const editorRef = useRef(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sharedFileId, setSharedFileId] = useState(null);
  const [currentFileSeq, setCurrentFileSeq] = useState(null);
  const { store } = useContext(GlobalContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value);
      if (endDate && new Date(value) > new Date(endDate)) {
        alert('시작 날짜는 종료 날짜보다 늦을 수 없습니다.');
        setStartDate('');
      }
    } else {
      if (!startDate) {
        alert('시작 날짜를 먼저 선택해주세요.');
        return;
      }
      if (new Date(value) < new Date(startDate)) {
        alert('종료 날짜는 시작일 보다 빠를 수 없습니다.');
        return;
      }
      setEndDate(value);
    }
  };

  const handleBackToList = () => {
    resetFileState();
    setTitle('');
    setContent('');
    setSelectedQuestion(null);
    resetFileState();
    handleSearch();
    setIsDetailView(false);
  };

  const handleFileUpload = uploadedUrl => {
    if (editorRef.current) {
      const editorInstance = editorRef.current.getInstance();
      const currentContent = editorInstance.getMarkdown();
      const imageMarkdown = `![이미지 설명](${uploadedUrl})`;
      editorInstance.setMarkdown(`${currentContent}\n${imageMarkdown}`);
    }
  };

  const resetFileState = () => {
    setSelectedFiles([]);
    setUploadedFiles([]);
    setSharedFileId(null);
    setCurrentFileSeq(null);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const url = '/cs/category/list.do';
        const response = await nlPost(url, {});
        if (response.length > 0) {
          const filteredCategories = response.filter(cat => cat.useYn === 'Y');
          setCategories(filteredCategories);
        }
      } catch (error) {}
    };

    const initialize = async () => {
      await fetchCategories();
      handleSearch();
    };
    initialize();
  }, []);

  const handleSubmit = async () => {
    const content = editorRef.current.getInstance().getMarkdown();
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (title.length > 33) {
      alert('제목은 33글자를 초과할 수 없습니다.');
      return;
    }
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    if (content.length > 1000) {
      alert('내용은 1000글자를 초과할 수 없습니다.');
      return;
    }

    const isUpdateMode = selectedQuestion?.qstnStts === 'Y';
    const apiUrl = isUpdateMode
      ? '/cs/ask/answer/update.do'
      : '/cs/ask/answer/create.do';
    const payload = {
      ansTtl: title,
      ansCn: content,
      entSn: store.entSn,
      fileId: sharedFileId,
      ...(isUpdateMode && { ansId: selectedQuestion?.ansId }),
      ...(selectedQuestion && { qstnId: selectedQuestion.qstnId }),
    };

    try {
      await dbPost(apiUrl, payload);
      alert(`답변이 성공적으로 ${isUpdateMode ? '수정' : '등록'}되었습니다.`);

      try {
        const updateStatusUrl = '/cs/ask/answerStatus.do';
        await dbPost(updateStatusUrl, { qstnId: selectedQuestion.qstnId });
      } catch (updateError) {
        alert('답변 상태 업데이트 중 문제가 발생했습니다.');
      }

      resetFileState();
      setTitle('');
      setContent('');
      handleSearch();
      setIsDetailView(false);
    } catch (error) {
      alert(`답변 ${isUpdateMode ? '수정' : '등록'} 중 문제가 발생했습니다.`);
    }
  };

  const fetchQuestions = async (filters = {}) => {
    try {
      const url = '/cs/ask/search.do';
      const response = await nlPost(url, filters);
      setQuestions(Array.isArray(response) ? response : []);
    } catch (error) {
      setQuestions([]);
    }
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const paginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return questions.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleSearch = (period = selectedPeriod) => {
    const filters = {
      ctgryId: selectedCategory || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      userSn: store?.authrtId === 'ADMIN' ? 'ADMIN' : store.userSn || null,
    };

    if (period && period !== 'ALL') {
      const parsedPeriod = parseInt(period);
      if (isNaN(parsedPeriod)) {
        return;
      }

      const today = new Date();
      const startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - parsedPeriod);

      filters.startDate = startDate
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '.');
      filters.endDate = today.toISOString().split('T')[0].replace(/-/g, '.');
    }

    fetchQuestions(filters);
  };

  const handleDetailClick = async question => {
    setSelectedQuestion(question);
    setIsDetailView(true);
    if (question.qstnStts === 'Y') {
      try {
        const url = '/cs/ask/answer/view.do';
        const response = await nlPost(url, { qstnId: question.qstnId });
        if (response) {
          setTitle(response.ansTtl || '');
          setContent(response.ansCn || '');
          setAnsRegDt(response.regDt || '');
          // setFile(response.fileId || null);
          setSelectedQuestion(prev => ({
            ...prev,
            ansId: response.ansId,
          }));
        }
      } catch (error) {}
    } else {
      setTitle('');
      setContent('');
    }
  };

  const renderList = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">문의내역</h2>
      </div>
      <ul className="guide-txt">
        <li>내가 문의했던 내용과 답변을 조회할 수 있습니다.</li>
      </ul>
      <div className="tab full fill tab-mutation">
        <ul role="tablist">
          {['1', '3', '6', 'ALL'].map((period, index) => (
            <li
              key={index}
              id={`tab_0${index}`}
              role="tab"
              aria-selected={selectedPeriod === period}
              className={selectedPeriod === period ? 'active' : ''}
            >
              <button
                type="button"
                className="btn-tab"
                onClick={() => {
                  setSelectedPeriod(period);
                  handleSearch(period);
                }}
              >
                {period === 'ALL' ? '전체' : `${period}개월`}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="search-top-box">
        <div className="sch-filter-box flex">
          <div className="filter-form">
            <label
              className="label"
              htmlFor="appl-sch-sel1"
              id="appl-sch-sel1-lbl"
            >
              카테고리
            </label>
            <select
              id="appl-sch-sel1"
              className="form-select md"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              aria-labelledby="appl-sch-sel1-lbl"
            >
              <option value="">전체</option>
              {categories
                .filter(cat => cat.ctgryId !== 'NOTICE')
                .map(cat => (
                  <option key={cat.ctgryId} value={cat.ctgryId}>
                    {cat.ctgryNm}
                  </option>
                ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label
                className="hidden-label"
                htmlFor="startDate"
                id="startDateLbl"
              >
                조회 기간 시작
              </label>
              <input
                type="date"
                aria-labelledby="startDateLbl"
                className="form-control"
                value={startDate}
                onChange={e => handleDateChange('start', e.target.value)}
                id="startDate"
              />
              ~
              <label className="hidden-label" htmlFor="endDate" id="endDateLbl">
                조회 기간 끝
              </label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => handleDateChange('end', e.target.value)}
                aria-labelledby="endDateLbl"
                id="endDate"
              />
            </div>
            <button
              type="button"
              className="btn md primary"
              onClick={() => handleSearch()}
            >
              검색
            </button>
          </div>
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl col data scroll">
          <caption>내 문의 내역을 조회할 수 있는 테이블입니다.</caption>
          <thead>
            <tr>
              <th>카테고리</th>
              <th>문의사항</th>
              <th>등록일</th>
              <th>상태</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData().map(q => (
              <tr
                key={q.qstnId}
                onClick={() => handleDetailClick(q)}
                className="clickable-row"
              >
                <td>
                  <span className="krds-badge outline">
                    {categories.find(cat => cat.ctgryId === q.ctgryId)
                      ?.ctgryNm || '미정'}
                  </span>
                </td>
                <td>
                  <strong>{q.qstnTtl}</strong>
                </td>
                <td>{q.regDt.split(' ')[0]}</td>
                <td>
                  <span
                    className={`krds-badge ${
                      q.qstnStts === 'Y' ? 'bg3' : 'bg1'
                    }`}
                  >
                    {q.qstnStts === 'Y' ? '답변완료' : '미답변'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <GbitTablePaging
        totalItems={questions.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
      {store?.authrtId !== 'ADMIN' && store?.logined && (
        <div className="page-btn-wrap">
          <button
            type="button"
            onClick={() => navigate('/cs/question/regQuestion')}
            className="btn xlg primary"
          >
            문의하기
          </button>
        </div>
      )}
    </div>
  );

  const renderDetailView = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">문의사항 상세보기</h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl data row">
          <tbody>
            <tr>
              <th>카테고리</th>
              <td>
                <div className="input-group">
                  {categories.find(
                    cat => cat.ctgryId === selectedQuestion?.ctgryId
                  )?.ctgryNm || '미정'}
                </div>
              </td>
            </tr>
            <tr>
              <th>등록일</th>
              <td>
                <div className="input-group">{selectedQuestion?.regDt}</div>
              </td>
            </tr>
            <tr>
              <th>제목</th>
              <td>
                <div className="input-group">{selectedQuestion?.qstnTtl}</div>
              </td>
            </tr>
            <tr>
              <th>내용</th>
              <td>
                <div className="input-group">
                  <TextViewer
                    content={
                      selectedQuestion
                        ? selectedQuestion?.qstnCn
                        : '내용이 없습니다.'
                    }
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!(store?.authrtId !== 'ADMIN' && selectedQuestion?.qstnStts === 'N') && (
        <div className="contents">
          <div className="tbl-wrap">
            <table className="tbl data row">
              <tbody>
                {!(
                  store?.authrtId === 'ADMIN' &&
                  selectedQuestion?.qstnStts === 'N'
                ) && (
                  <tr>
                    <th>등록일</th>
                    <td>
                      <label className="hidden-label" htmlFor="ansRegDt">
                        라벨
                      </label>
                      <div className="input-group">20{ansRegDt}</div>
                    </td>
                  </tr>
                )}
                <tr>
                  <th>제목</th>
                  <td>
                    <label className="hidden-label" htmlFor="title">
                      라벨
                    </label>
                    {store?.authrtId !== 'ADMIN' &&
                    selectedQuestion?.qstnStts === 'Y' ? (
                      <div className="input-group">
                        {selectedQuestion.qstnTtl}
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="제목을 입력해주세요."
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        aria-labelledby="title"
                        id="title"
                      />
                    )}
                  </td>
                </tr>
                <tr>
                  <th>내용</th>
                  <td>
                    {store?.authrtId !== 'ADMIN' &&
                    selectedQuestion?.qstnStts === 'Y' ? (
                      <TextViewer content={content} />
                    ) : (
                      <TextEditor
                        className="form-control"
                        ref={editorRef}
                        initialValue={content}
                      />
                    )}
                  </td>
                </tr>
                {store?.authrtId === 'ADMIN' && (
                  <tr>
                    <th>첨부파일</th>
                    <td>
                      <div className="input-group">
                        <button
                          type="button"
                          className="btn tertiary"
                          onClick={() => {
                            setIsFileModalOpen(true);
                          }}
                        >
                          파일 첨부
                        </button>
                      </div>
                      {uploadedFiles.length > 0 && (
                        <p>첨부된 파일 개수: {uploadedFiles.length}개</p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div
        className="page-btn-wrap both"
        style={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <button
          type="button"
          className="btn xlg tertiary"
          onClick={handleBackToList}
        >
          목록으로
        </button>

        {store?.authrtId === 'ADMIN' && (
          <button
            type="button"
            className="btn xlg primary"
            onClick={handleSubmit}
          >
            {selectedQuestion?.qstnStts === 'Y' ? '답변 수정' : '답변 등록'}
          </button>
        )}
      </div>

      {isFileModalOpen && (
        <FileUpload
          onClose={() => setIsFileModalOpen(false)}
          dbPost={dbPost}
          dbForm={dbForm}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          sharedFileId={sharedFileId}
          setSharedFileId={setSharedFileId}
          currentFileSeq={currentFileSeq}
          setCurrentFileSeq={setCurrentFileSeq}
          onUpload={handleFileUpload}
        />
      )}
    </div>
  );

  return (
    <div className="inner in-between">
      {isDetailView ? renderDetailView() : renderList()}
    </div>
  );
};

export default QuestionList;
